import { Link, useLocation } from 'react-router-dom';
import { Activity, BarChart2, Brain, TrendingUp, Globe, Newspaper, LogOut, User, Shield, Menu, X, Clock, Coins, GraduationCap } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useEffect } from 'react';
import api from '@/utils/api';
import NotificationsBell from './NotificationsBell';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_f667072a-4bba-4bc4-a4a6-89cc86ea065f/artifacts/bznalkms_4521201e-8f5a-4754-bfbe-d698baeab88c.jpg";

export const Navigation = () => {
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [subInfo, setSubInfo] = useState(null);
  const [adminUnread, setAdminUnread] = useState(0);

  const isActive = (path) => location.pathname === path;
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) return;
    api.get('/subscription/status')
      .then(res => setSubInfo(res.data))
      .catch(() => {});
  }, [isAdmin, user?.subscription_status]);

  // Admin: check for unread messages
  useEffect(() => {
    if (!isAdmin) return;
    const check = () => {
      api.get('/admin/chat/conversations')
        .then(res => {
          const total = (res.data || []).reduce((sum, c) => sum + (c.unread_count || 0), 0);
          setAdminUnread(total);
        })
        .catch(() => {});
    };
    check();
    const interval = setInterval(check, 15000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // عداد الاشتراك
  const SubBadge = () => {
    if (isAdmin || !subInfo) return null;
    const { status, remaining_days } = subInfo;
    const isTrial = false;
    const isLow = remaining_days <= 3;
    const bgColor = isTrial ? 'bg-amber-500/20 border-amber-500/50 text-amber-400' 
                   : isLow ? 'bg-red-500/20 border-red-500/50 text-red-400'
                   : 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400';
    const label = isTrial ? (language === 'ar' ? 'تجربة' : 'Trial') : '';
    return (
      <Link to="/subscribe" className={`flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] sm:text-xs font-bold ${bgColor} hover:opacity-80 transition-opacity`} data-testid="sub-badge">
        <Clock className="w-3 h-3" />
        <span>{remaining_days}</span>
        <span className="hidden sm:inline">{language === 'ar' ? 'يوم' : 'days'}</span>
        <span className="sm:hidden">{language === 'ar' ? 'ي' : 'd'}</span>
      </Link>
    );
  };

  return (
    <nav className="sticky top-0 z-[60] glass-panel border-b border-white/10">
      <div className="container mx-auto px-2 sm:px-4">
        <div className="flex items-center justify-between h-14 sm:h-16">
          
          {/* Logo */}
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3" onClick={closeMobileMenu}>
              <img 
                src={LOGO_URL} 
                alt="ALROYAH Logo" 
                className="w-9 h-9 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-[#00E5FF]/50"
                loading="eager"
              />
              <span className="hidden sm:block text-base sm:text-xl font-black tracking-tight bg-gradient-to-r from-[#00E5FF] to-white bg-clip-text text-transparent">ALROYAH</span>
            </Link>
            
            {/* User Name - Mobile Only - مرحبا + الاسم */}
            {user && (
              <div className="flex sm:hidden items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-[#00E5FF]/20 to-[#00E5FF]/5 rounded-full border border-[#00E5FF]/40">
                <span className="text-[9px] text-zinc-400">{language === 'ar' ? 'مرحباً' : 'Hi'}</span>
                <span className="text-[11px] font-bold text-[#00E5FF] max-w-[80px] truncate">
                  {user.first_name || user.email?.split('@')[0]}
                </span>
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <div className="hidden sm:flex items-center space-x-0.5 sm:space-x-1">
            <Link
              to="/"
              data-testid="nav-dashboard"
              className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-sm text-xs sm:text-sm font-medium transition-all ${
                isActive('/')
                  ? 'bg-white/10 text-white'
                  : 'text-zinc-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Activity className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{t('nav.dashboard')}</span>
            </Link>
            {isAdmin && (
            <Link
              to="/performance"
              data-testid="nav-performance"
              className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-sm text-xs sm:text-sm font-medium transition-all flex items-center ${
                isActive('/performance')
                  ? 'bg-white/10 text-white'
                  : 'text-zinc-400 hover:text-white hover:bg-white/5'
              }`}
              title={language === 'ar' ? 'الأداء' : 'Performance'}
            >
              <BarChart2 className="w-4 h-4 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline ml-2">{t('nav.performance')}</span>
            </Link>
            )}
            <Link
              to="/gold"
              data-testid="nav-gold"
              className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-sm text-xs sm:text-sm font-medium transition-all ${
                isActive('/gold')
                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                  : 'text-yellow-400/80 hover:text-yellow-300 hover:bg-yellow-500/10'
              }`}
            >
              <Coins className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'ar' ? 'الذهب' : 'Gold'}</span>
            </Link>
            <Link
              to="/ai-deals"
              data-testid="nav-ai-deals"
              className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-sm text-xs sm:text-sm font-medium transition-all ${
                isActive('/ai-deals')
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                  : 'text-purple-400 hover:text-purple-300 hover:bg-purple-500/10'
              }`}
            >
              <Brain className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'ar' ? 'صفقات الذكاء' : 'AI Deals'}</span>
            </Link>

            <Link
              to="/my-trades"
              data-testid="nav-my-trades"
              className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-sm text-xs sm:text-sm font-medium transition-all ${
                isActive('/my-trades')
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50'
                  : 'text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10'
              }`}
            >
              <Activity className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'ar' ? 'صفقاتي' : 'My Trades'}</span>
            </Link>
            
            {/* Learning Center — visible to all users */}
            <Link
              to="/learn"
              data-testid="nav-learn"
              className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-sm text-xs sm:text-sm font-medium transition-all ${
                isActive('/learn')
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                  : 'text-emerald-300/80 hover:text-emerald-200 hover:bg-emerald-500/10'
              }`}
              title={language === 'ar' ? 'مركز التعليم' : 'Learning Center'}
            >
              <GraduationCap className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'ar' ? 'تعليم' : 'Learn'}</span>
            </Link>

            {/* Admin Link - Desktop */}
            {isAdmin && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className={`relative px-2 sm:px-4 py-1.5 sm:py-2 rounded-sm text-xs sm:text-sm font-medium transition-all ${
                  isActive('/admin')
                    ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                    : 'text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/30'
                }`}
              >
                <Shield className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{language === 'ar' ? 'التحكم' : 'Admin'}</span>
                {adminUnread > 0 && (
                  <span className="absolute -top-1 -right-1 bg-cyan-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-bold animate-pulse">{adminUnread}</span>
                )}
              </Link>
            )}

            {/* Scalp Bot link removed per product decision — replaced by /learn */}
          </div>

          {/* Desktop Right Side */}
          <div className="hidden sm:flex items-center space-x-1 sm:space-x-3">
            <SubBadge />
            <button
              onClick={toggleLanguage}
              className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-sm text-xs sm:text-sm font-medium text-zinc-400 hover:text-white hover:bg-white/5 transition-all"
              data-testid="language-toggle"
            >
              <Globe className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{language === 'en' ? 'العربية' : 'English'}</span>
            </button>
            
            {/* User Info - Desktop */}
            {user && (
              <div className="flex items-center space-x-2">
                <NotificationsBell />
                <span className="hidden md:flex items-center text-xs text-zinc-400">
                  <User className="w-3 h-3 mr-1" />
                  {user.first_name}
                </span>
                <button
                  onClick={handleLogout}
                  className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-sm text-xs sm:text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all"
                  data-testid="logout-btn"
                  title={language === 'ar' ? 'تسجيل الخروج' : 'Logout'}
                >
                  <LogOut className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </div>
            )}
            
            <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-red-500/20 text-red-500 border border-red-500/50 rounded-full text-[8px] sm:text-[10px] font-mono uppercase animate-pulse">
              ● <span className="hidden sm:inline">{t('nav.live')}</span>
            </span>
          </div>

          {/* Mobile Right Side */}
          <div className="flex sm:hidden items-center gap-2">
            <SubBadge />
            {user && <NotificationsBell />}
            {/* Live Badge - Mobile */}
            <span className="px-1.5 py-0.5 bg-red-500/20 text-red-500 border border-red-500/50 rounded-full text-[8px] font-mono uppercase animate-pulse flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
              LIVE
            </span>
            
            {/* Hamburger Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-sm text-zinc-400 hover:text-white hover:bg-white/10 transition-all"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="sm:hidden absolute top-full left-0 right-0 z-[70] bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/10 shadow-2xl">
          <div className="container mx-auto px-3 py-4">
            {/* Navigation Links */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <Link
                to="/"
                data-testid="nav-dashboard-mobile"
                onClick={closeMobileMenu}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-all flex items-center gap-3 ${
                  isActive('/')
                    ? 'bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/30'
                    : 'bg-zinc-800/50 text-zinc-300 hover:bg-zinc-700/50 border border-transparent'
                }`}
              >
                <Activity className="w-5 h-5" />
                <span>{language === 'ar' ? 'الرئيسية' : 'Home'}</span>
              </Link>
              {isAdmin && (
              <Link
                to="/performance"
                data-testid="nav-performance-mobile"
                onClick={closeMobileMenu}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-all flex items-center gap-3 ${
                  isActive('/performance')
                    ? 'bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/30'
                    : 'bg-zinc-800/50 text-zinc-300 hover:bg-zinc-700/50 border border-transparent'
                }`}
              >
                <BarChart2 className="w-5 h-5" />
                <span>{language === 'ar' ? 'الأداء' : 'Performance'}</span>
              </Link>
              )}
              <Link
                to="/gold"
                data-testid="nav-gold-mobile"
                onClick={closeMobileMenu}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-all flex items-center gap-3 ${
                  isActive('/gold')
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                    : 'bg-zinc-800/50 text-yellow-400 hover:bg-yellow-500/10 border border-transparent'
                }`}
              >
                <Coins className="w-5 h-5" />
                <span>{language === 'ar' ? 'الذهب' : 'Gold'}</span>
              </Link>
              <Link
                to="/ai-deals"
                data-testid="nav-ai-deals-mobile"
                onClick={closeMobileMenu}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-all flex items-center gap-3 ${
                  isActive('/ai-deals')
                    ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                    : 'bg-zinc-800/50 text-purple-400 hover:bg-purple-500/10 border border-transparent'
                }`}
              >
                <Brain className="w-5 h-5" />
                <span>{language === 'ar' ? 'صفقات الذكاء' : 'AI Deals'}</span>
              </Link>
              <Link
                to="/learn"
                data-testid="nav-learn-mobile"
                onClick={closeMobileMenu}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-all flex items-center gap-3 col-span-2 ${
                  isActive('/learn')
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                    : 'bg-zinc-800/50 text-emerald-300 hover:bg-emerald-500/10 border border-transparent'
                }`}
              >
                <GraduationCap className="w-5 h-5" />
                <span>{language === 'ar' ? 'مركز التعليم — تعلّم كيف تتداول' : 'Learning Center'}</span>
              </Link>
            </div>
            
            {/* Admin Link - Mobile */}
            {isAdmin && (
              <Link
                to="/admin"
                data-testid="nav-admin-mobile"
                onClick={closeMobileMenu}
                className={`relative w-full mb-4 px-4 py-3 rounded-lg text-sm font-medium transition-all flex items-center gap-3 ${
                  isActive('/admin')
                    ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                    : 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30'
                }`}
              >
                <Shield className="w-5 h-5" />
                <span>{language === 'ar' ? 'لوحة التحكم' : 'Admin Panel'}</span>
                {adminUnread > 0 && (
                  <span className="bg-cyan-500 text-white text-[10px] rounded-full px-1.5 py-0.5 font-bold animate-pulse mr-auto">{adminUnread} رسالة</span>
                )}
              </Link>
            )}

            {/* Divider */}
            <div className="border-t border-white/10 my-3"></div>

            {/* Bottom Actions */}
            <div className="flex items-center justify-between">
              {/* Language Toggle */}
              <button
                onClick={() => {
                  toggleLanguage();
                  closeMobileMenu();
                }}
                className="px-4 py-2 rounded-lg bg-zinc-800/50 text-sm font-medium text-zinc-300 hover:bg-zinc-700/50 transition-all flex items-center gap-2"
                data-testid="language-toggle-mobile"
              >
                <Globe className="w-4 h-4" />
                <span>{language === 'en' ? 'العربية' : 'English'}</span>
              </button>
              
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="px-4 py-2 rounded-lg bg-red-500/10 text-sm font-medium text-red-400 hover:bg-red-500/20 transition-all flex items-center gap-2 border border-red-500/30"
                data-testid="logout-btn-mobile"
              >
                <LogOut className="w-4 h-4" />
                <span>{language === 'ar' ? 'خروج' : 'Logout'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
